import { useEffect, useState } from 'react';
import loadable from '@loadable/component';
import 'rxjs/add/operator/distinctUntilChanged';

import moduleLocator from 'moduleLocator';
import { loadBetsHistoryModule } from 'modules/bets-history/async';
import { loadBetslipModule } from 'modules/betslip/async';
import { loadCashoutModule } from 'modules/cashout/async';
import { loadFiltersModule } from 'modules/filters/async';
import { loadFreebetModule } from 'modules/freebet/async';
import { loadMatchModule } from 'modules/match/async';
import { loadOutrightModule } from 'modules/outright/async';
import { loadStreamModule } from 'modules/stream/async';
import type { AsyncModules, ModuleName, Modules } from './types';

export type {
    AsyncModules,
    BetslipModule,
    ModuleLocator,
    Modules,
} from './types';

export const asyncModuleLoaders: AsyncModules = {
    betslip: loadable.lib(loadBetslipModule),
    outright: loadable.lib(loadOutrightModule),
    match: loadable.lib(loadMatchModule),
    freebet: loadable.lib(loadFreebetModule),
    stream: loadable.lib(loadStreamModule),
    filters: loadable.lib(loadFiltersModule),
    'bets-history': loadable.lib(loadBetsHistoryModule),
    cashout: loadable.lib(loadCashoutModule),
};

export function useAsyncModule<T extends ModuleName>(
    moduleName: T,
    preload?: boolean
): Modules[T]['default'] | null {
    const [module, setModule] = useState<Modules[T]['default'] | null>(
        moduleLocator.getImmediately<T>(moduleName)
    );

    useEffect(() => {
        if (!module && preload) {
            asyncModuleLoaders[moduleName].load();
        }
    }, [module, moduleName, preload]);

    useEffect(() => {
        if (module) return;

        const sub = moduleLocator
            .get<T>(moduleName)
            .distinctUntilChanged()
            .subscribe((subModule: Modules[T]['default']) => {
                setModule(subModule);
            });

        return () => sub.unsubscribe();
    }, [moduleName, module]);

    return module;
}
